import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ColumnSummary from '../../widgets/ColumnSummary';
import SitePenLogo from '../../widgets/SitepenLogo';

const code = require('../../assets/images/illustration/code-reuse.svg');
const analysisImg = require('../../assets/images/illustration/analysis.svg');
const benefitsImg = require('../../assets/images/illustration/engineering.svg');
const jmeterImg = require('../../assets/images/logo/small/jmeter.svg');
const k6Img = require('../../assets/images/logo/small/k6.svg');
const loadNinjaImg = require('../../assets/images/logo/small/load-ninja.svg');
const loadTestingImg = require('../../assets/images/icon-illustration/load-testing.svg');
const soakTestingImg = require('../../assets/images/icon-illustration/soak-testing.svg');
const spikeTestingImg = require('../../assets/images/icon-illustration/spike-testing.svg');
const stressTestingImg = require('../../assets/images/icon-illustration/accelerated-results.svg');
const teamEfficiencyImg = require('../../assets/images/illustration/team-efficiency.svg');

const factory = create();

export const PerformanceTesting = factory(function PerformanceTesting() {
	return (
		<div>
			<Head
				title="Performance Testing | SitePen"
				description="Web development company building enterprise solutions to improve processes and empower business success utilizing performance testing and quality assurance best practices."
			/>

			<Hero background="level3Red">
				{{
					xlTitle: (
						<div>
							Performance
							<br />
							Testing
						</div>
					),
					blurb: `Today's sophisticated business applications need to support fast-moving transactions. Performance analysis and testing ensure that your tools meet your users' demanding expectations.`
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<SummaryList
						trailingBias="medium"
						listGuttering="none"
						style="alternating-reverse"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Solidify Your System With Performance Analysis',
									blurb: (
										<virtual>
											<p>
												When web applications were first introduced as a
												tool to support business processes, they
												automatically provided dramatic improvements in
												throughput by reducing calculation times and
												virtually eliminating wait times between process
												steps. Today's business environment has continued to
												accelerate, so software systems need to ensure that
												they perform as quickly as possible.
											</p>
											<p>
												Clear business requirements, excellent application
												architecture, and high-quality coding, while
												essential, are not sufficient to ensure that
												applications meet their performance targets.
												Performance analysis and testing have become
												critical tools to guarantee application performance
												requirements are achieved.
											</p>
										</virtual>
									),
									image: <Image src={analysisImg} size="lg" />
								},
								{
									title: 'Why Develop with SitePen',
									blurb: (
										<virtual>
											<p>
												At SitePen, we believe that quality is the single,
												most important aspect of any project we undertake.
												Our commitment to delivering bug-free solutions
												infuses every part of our development activities.
											</p>
											<p>
												You will start to experience this as soon as you
												reach out to us. Our Sales and Project Management
												teams work with you to understand your goals and
												expectations, identifying the characteristics that
												have the most significant influence on the
												application's quality.
											</p>
											<p>
												Our engineering team will then develop the solution
												with these goals in mind. They also create extensive
												suites of automated tests that ensure that the code
												continues to work as intended. Finally, we
												continuously monitor the project in search of
												refactoring opportunities to improve the code's
												structure as it evolves. While not usually
												considered an essential aspect of quality assurance,
												we have found that superior code organization often
												leads to exceptional quality in the final product.
											</p>
										</virtual>
									),
									image: <SitePenLogo />
								},
								{
									blurb: (
										<virtual>
											<p>
												As the application approaches completion, we will
												shift the project to examine the quality of the
												solution. During this phase, we conduct tests that
												exercise all aspects of the complete solution. We
												verify the business logic's correctness, ensure the
												code is performant and will not bog down under
												real-world workloads, and harden the application
												against the vast array of security threats that
												today's applications face.
											</p>
											<p>
												Naturally, the final decision concerning the quality
												of the application resides with you. We will work
												with your infrastructure team to deploy the solution
												to a staging environment that allows you to conduct
												your own quality assurance verification. When all
												findings have been identified, we will resolve them,
												ensuring that you receive the most reliable and
												accurate application possible.
											</p>
										</virtual>
									),
									image: <Image src={code} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<ColumnSummary columnCount={2}>
						{{
							title: 'Types of Performance Testing',
							blurb: (
								<virtual>
									Performance is an overloaded term when speaking about web
									applications. A successful web application must be able to
									function under many different scenarios. Several different types
									of performance tests are available to verify the system's
									ability to operate quickly and stably in production.
								</virtual>
							),
							items: [
								{
									title: 'Load Testing',
									blurb: (
										<virtual>
											Load, or volume, testing is a type of performance
											analysis that measures an application's ability to
											function when exposed to real-world workloads. Usually,
											one or more test clients are configured to send
											realistic requests to the system-under-test. Metrics are
											gathered, including response times, to measure that the
											application manages multiple concurrent requests without
											degrading the users' experience.
										</virtual>
									),
									image: (
										<Image
											align="center"
											size="icon"
											src={loadTestingImg}
											constrainedBy="height"
											lazy
										/>
									)
								},
								{
									title: 'Stress Testing',
									blurb: (
										<virtual>
											Stress testing is an extreme form of load testing. Load
											testing analyzes a system's ability to handle realistic
											workloads. In contrast, stress testing attempts to find
											the limits of the system. This type of test is often
											used to measure the excess capacity of the system and
											its infrastructure, allowing the business to balance the
											system's ability to absorb unusually high activity with
											the capacity of the underlying infrastructure.
										</virtual>
									),
									image: (
										<Image
											align="center"
											size="icon"
											src={stressTestingImg}
											constrainedBy="height"
											lazy
										/>
									)
								},
								{
									title: 'Spike Testing',
									blurb: (
										<virtual>
											Many systems perform well subjected to stable loading.
											However, a sudden increase applies transient stress to a
											system that can cause degraded user experiences. Spike
											tests are used to simulate these sudden increases in
											demand. These tests are often used on systems that can
											elastically scale their infrastructure to confirm that
											the scaling apparatus can respond quickly enough to
											maintain the system's quality of service in the face of
											increasing demand while also reducing resources when
											demand drops sharply.
										</virtual>
									),
									image: (
										<Image
											align="center"
											size="icon"
											src={spikeTestingImg}
											constrainedBy="height"
											lazy
										/>
									)
								},
								{
									title: 'Soak Testing',
									blurb: (
										<virtual>
											Soak testing, also known as endurance testing, confirms
											that a system can perform under long-term, stable
											conditions. This type of testing is ideal for finding
											sporadic bugs, often caused by difficult to trace race
											conditions in the software. It is also very good at
											confirming that the system is appropriately releasing
											resources that it no longer requires. Resource
											monitoring is a crucial component of soak testing since
											it can highlight gradual increases in resource
											consumption that can eventually lead to degraded
											performance or system failure.
										</virtual>
									),
									image: (
										<Image
											align="center"
											size="icon"
											src={soakTestingImg}
											constrainedBy="height"
											lazy
										/>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Performance Testing Tools',
							items: [
								{
									title: 'JMeter',
									blurb: (
										<p>
											JMeter is an open-source project that is designed to
											load test web applications. It provides a test
											development model where test engineers can precisely
											control how the target application will be tested. Once
											the tests are developed, load tests can be automatically
											executed and analyzed via HTML-based load reports.
										</p>
									),
									image: <Image src={jmeterImg} lazy size="icon" align="center" />
								},
								{
									title: 'k6',
									blurb: (
										<p>
											k6 is another popular load testing tool for web services
											and web applications. It offers a complete set of
											capabilities for writing tests that ensure that the
											application performs well in various loading conditions,
											such as load testing to guarantee performance, stress
											tests to confirm system capacity, and soak tests to
											verify application stability over time.
										</p>
									),
									image: <Image src={k6Img} lazy size="icon" align="center" />
								},
								{
									title: 'LoadNinja',
									blurb: (
										<p>
											LoadNinja is a cloud-based performance testing platform
											that seeks to accelerate test development via a
											scriptless model. Instead of manually writing the
											performance tests, a recorder is used to capture a real
											user's interaction with the web application. These test
											scenarios can then be replayed on many real web browsers
											to provide highly representative test runs and
											sophisticated browser-based metrics for post-run
											analysis.
										</p>
									),
									image: (
										<Image src={loadNinjaImg} lazy size="icon" align="center" />
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse>
						{{
							title: 'Choosing the Right Software Performance Testing Team',
							blurb: (
								<virtual>
									<p>
										When selecting a performance testing team, there are three
										major characteristics to look for. The best performance
										testing teams can identify the most performance-sensitive
										aspects of the application. Most applications have a very
										small number of code-paths that form the critical
										performance chain of the application. Correctly identifying
										these paths allows the test developers to focus their
										efforts, ensuring that the application performs well without
										overloading the test suite with unnecessary performance
										checks.
									</p>
									<p>
										Performance testing teams should be familiar with the wide
										range of performance tests and tools available on the
										market. Inexperienced teams tend to use a large number of
										tools in an attempt to guarantee the application's
										performance. We believe that selecting the minimum number of
										tools that will provide the necessary verification is a
										better approach. When the right tools are employed
										correctly, all the essential metrics can be gathered while
										minimizing the test suite's complexity.
									</p>
									<p>
										Once performance metrics are gathered, and trouble spots are
										identified, a performance testing team should be able to
										analyze the application's source code to provide actionable
										recommendations for how to alleviate the performance
										concerns. This ability offers invaluable information to
										developers who can then immediately start creating solutions
										to improve performance.
									</p>
								</virtual>
							),
							image: <Image src={teamEfficiencyImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={benefitsImg} lazy size="lg" />,
							title: 'Benefits of Performance Testing Services',
							blurb: (
								<virtual>
									Without performance testing, most applications are only
									subjected to a few users at a time during development.
									Developers run individual instances as they create features, and
									business users often select a small team to verify application
									behavior. While these scenarios can verify the application's
									correctness, they are insufficient to confirm that an
									application will be stable and performant in real-world
									scenarios. Performance testing encompasses a range of loading
									and analysis techniques that ensure that the application will
									work as intended at scale.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Performance Testing FAQs">
					{[
						{
							title: 'What Are the Different Types of Performance Testing?',
							content: (
								<virtual>
									<p>
										There are two primary types of performance testing - loading
										and stability. While these can be broken down into
										subcategories, these types capture all performance testing
										and analysis intent.
									</p>
									<p>
										Load testing involves subjecting the application to a large
										number of transactions over a limited time. This type of
										testing aims to verify that the application can remain
										performant and responsive to users. These tests are often
										conducted over a relatively short time.
									</p>
									<p>
										Stability testing seeks to confirm that the system remains
										stable under a steady, long-term load. These tests are
										intended to ensure that the system does not have bugs that
										compromise the system's ability to remain functional over
										long periods. Stability tests are often used to identify
										data races that can introduce sporadic errors. They are also
										used to confirm that resource consumption remains stable
										(i.e., memory requirements and CPU loading remain stable
										over time).
									</p>
								</virtual>
							)
						},
						{
							title: 'Why Is Performance Testing Important?',
							content: (
								<p>
									Performance tests are often the first opportunity for a
									development team to confirm their assumptions about system
									architecture and infrastructure sizing for the application.
									During the initial development stages, the team is focused on
									requirements and business feature development. As the
									application nears completion, however, it can be subjected to
									performance testing to confirm that the system will run
									effectively under real-world loading conditions.
								</p>
							)
						},
						{
							title: 'When Should Performance Testing Be Done',
							content: (
								<p>
									Some performance testing can be done on critical code paths as
									they are identified, but most of this type of testing must wait
									until the system is nearly complete. When the primary features
									of the application are done, representative workflows can be
									completed. This completeness level is often necessary to truly
									understand how the system will respond after being deployed to
									production.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default PerformanceTesting;
